import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Table,
  ActionIcon,
  Loader,
  Center,
  ColorSwatch,
  Select,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconTrash,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  flagDefinitionsService,
  FlagDefinition,
} from "../../../services/flagDefinitionsService";

const MANTINE_COLORS = [
  "red",
  "pink",
  "grape",
  "violet",
  "indigo",
  "blue",
  "cyan",
  "teal",
  "green",
  "lime",
  "yellow",
  "orange",
];

const COLOR_MAP: Record<string, string> = {
  red: "#fa5252",
  pink: "#e64980",
  grape: "#be4bdb",
  violet: "#7950f2",
  indigo: "#4c6ef5",
  blue: "#228be6",
  cyan: "#15aabf",
  teal: "#12b886",
  green: "#40c057",
  lime: "#82c91e",
  yellow: "#fab005",
  orange: "#fd7e14",
};

export default function FlagsAdminPage() {
  const queryClient = useQueryClient();
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  const [editingFlag, setEditingFlag] = useState<FlagDefinition | null>(null);
  const [flagToToggle, setFlagToToggle] = useState<FlagDefinition | null>(null);
  const [flagToDelete, setFlagToDelete] = useState<FlagDefinition | null>(null);
  const [search, setSearch] = useState("");

  // Form state
  const [formName, setFormName] = useState("");
  const [formColor, setFormColor] = useState<string>("blue");

  const {
    data: flags = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["flagDefinitions"],
    queryFn: () => flagDefinitionsService.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: (input: { name: string; color: string }) =>
      flagDefinitionsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flagDefinitions"] });
      notifications.show({
        title: "Success",
        message: "Flag definition created",
        color: "green",
      });
      closeFormModal();
    },
    onError: (err: any) => {
      notifications.show({
        title: "Error",
        message:
          err?.response?.data?.error || err.message || "Failed to create flag",
        color: "red",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      name?: string;
      color?: string;
    }) => flagDefinitionsService.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flagDefinitions"] });
      notifications.show({
        title: "Success",
        message: "Flag definition updated",
        color: "green",
      });
      closeFormModal();
    },
    onError: (err: any) => {
      notifications.show({
        title: "Error",
        message:
          err?.response?.data?.error || err.message || "Failed to update flag",
        color: "red",
      });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => flagDefinitionsService.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flagDefinitions"] });
      closeConfirmModal();
      setFlagToToggle(null);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => flagDefinitionsService.reactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flagDefinitions"] });
      closeConfirmModal();
      setFlagToToggle(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => flagDefinitionsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flagDefinitions"] });
      notifications.show({
        title: "Success",
        message: "Flag definition deleted",
        color: "green",
      });
      closeDeleteModal();
      setFlagToDelete(null);
    },
    onError: (err: any) => {
      notifications.show({
        title: "Cannot delete",
        message:
          err?.response?.data?.error || err.message || "Failed to delete flag",
        color: "red",
      });
      closeDeleteModal();
      setFlagToDelete(null);
    },
  });

  const filteredFlags = useMemo(() => {
    if (!search) return flags;
    const q = search.toLowerCase();
    return flags.filter((f) => f.name.toLowerCase().includes(q));
  }, [flags, search]);

  const handleAdd = () => {
    setEditingFlag(null);
    setFormName("");
    setFormColor("blue");
    openFormModal();
  };

  const handleEdit = (flag: FlagDefinition) => {
    setEditingFlag(flag);
    setFormName(flag.name);
    setFormColor(flag.color);
    openFormModal();
  };

  const handleSubmit = () => {
    if (!formName.trim()) return;
    if (editingFlag) {
      updateMutation.mutate({
        id: editingFlag.id,
        name: formName.trim(),
        color: formColor,
      });
    } else {
      createMutation.mutate({ name: formName.trim(), color: formColor });
    }
  };

  const handleToggleActive = (flag: FlagDefinition) => {
    setFlagToToggle(flag);
    openConfirmModal();
  };

  const confirmToggleActive = () => {
    if (!flagToToggle) return;
    if (flagToToggle.isActive) {
      deactivateMutation.mutate(flagToToggle.id);
    } else {
      reactivateMutation.mutate(flagToToggle.id);
    }
  };

  const handleDelete = (flag: FlagDefinition) => {
    setFlagToDelete(flag);
    openDeleteModal();
  };

  const confirmDelete = () => {
    if (flagToDelete) {
      deleteMutation.mutate(flagToDelete.id);
    }
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Case Flags</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage configurable case flag definitions
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          <Paper p="md" withBorder>
            <Group>
              <TextInput
                placeholder="Search by flag name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftSection={<IconSearch size={16} />}
                style={{ flex: 1 }}
              />
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => refetch()}
                loading={isLoading}
                leftSection={<IconRefresh size={16} />}
              >
                Refresh
              </Button>
            </Group>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="md">
              <Text fw={500} size="lg">
                Flag Definitions ({filteredFlags.length})
              </Text>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredFlags.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search
                    ? "No flags match the current search"
                    : "No flag definitions found. Click 'Add New' to create one."}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 60 }}>Color</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th style={{ width: 90 }}>Status</Table.Th>
                      <Table.Th style={{ width: 150 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredFlags.map((flag) => (
                      <Table.Tr
                        key={flag.id}
                        style={{ opacity: flag.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <ColorSwatch
                            color={COLOR_MAP[flag.color] || flag.color}
                            size={20}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Badge color={flag.color} variant="light" size="sm">
                              {flag.name}
                            </Badge>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={flag.isActive ? "green" : "red"}
                            variant="light"
                            size="sm"
                          >
                            {flag.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(flag)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={
                                flag.isActive ? "Deactivate" : "Reactivate"
                              }
                            >
                              <ActionIcon
                                variant="subtle"
                                color={flag.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(flag)}
                              >
                                {flag.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDelete(flag)}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={
            editingFlag ? "Edit Flag Definition" : "Add New Flag Definition"
          }
          size="md"
          centered
        >
          <Stack gap="md">
            <TextInput
              label="Flag Name"
              placeholder="e.g. Malignancy – Esophagus"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              maxLength={80}
              required
            />
            <Select
              label="Color"
              data={MANTINE_COLORS.map((c) => ({
                value: c,
                label: c.charAt(0).toUpperCase() + c.slice(1),
              }))}
              value={formColor}
              onChange={(val) => setFormColor(val || "blue")}
              renderOption={({ option }) => (
                <Group gap="sm">
                  <ColorSwatch
                    color={COLOR_MAP[option.value] || option.value}
                    size={16}
                  />
                  <Text size="sm">{option.label}</Text>
                </Group>
              )}
              required
            />
            {formColor && (
              <Group gap="sm">
                <Text size="sm" c="dimmed">
                  Preview:
                </Text>
                <Badge color={formColor} variant="light">
                  {formName || "Flag Name"}
                </Badge>
              </Group>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                loading={createMutation.isPending || updateMutation.isPending}
                disabled={!formName.trim()}
              >
                {editingFlag ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Modal */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={flagToToggle?.isActive ? "Deactivate Flag" : "Reactivate Flag"}
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {flagToToggle?.isActive ? "deactivate" : "reactivate"} the flag{" "}
              <strong>{flagToToggle?.name}</strong>?
            </Text>
            {flagToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated flags will no longer appear in the case flag
                selector, but will remain visible on cases that already have
                them.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={flagToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={
                  deactivateMutation.isPending || reactivateMutation.isPending
                }
              >
                {flagToToggle?.isActive ? "Deactivate" : "Reactivate"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Delete Flag Definition"
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to permanently delete the flag{" "}
              <strong>{flagToDelete?.name}</strong>? This action cannot be
              undone.
            </Text>
            <Text size="sm" c="dimmed">
              If this flag is applied to any cases, deletion will be blocked.
              Deactivate it instead.
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
